import { useMemo, useState } from "react";
import { copyToClipboard, showToast } from "../../../libraries/clipboardUtils";

const formatIsoDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00.000Z`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const randomFrom = (chars) => chars[Math.floor(Math.random() * chars.length)];

const generateLicenseKey = () => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const alnum = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const suffix = Array.from({ length: 7 }, () => randomFrom(alnum));
  suffix[0] = randomFrom(letters);
  suffix[1] = randomFrom("0123456789");

  // Shuffle to avoid fixed letter/number positions after '-'.
  for (let i = suffix.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = suffix[i];
    suffix[i] = suffix[j];
    suffix[j] = temp;
  }

  return `${randomFrom(letters)}${randomFrom("0123456789")}-${suffix.join("")}`;
};

function OnboardingObject({ branchName, adminEmail, adminPhone, branchId }) {
  const [copiedInitial, setCopiedInitial] = useState(false);
  const [copiedSecond, setCopiedSecond] = useState(false);
  const [copiedThird, setCopiedThird] = useState(false);
  const today = formatIsoDateOnly(new Date());
  const startDate = formatIsoDateOnly(new Date());
  const endDate = formatIsoDateOnly(addDays(new Date(), 30));
  const licenseKey = useMemo(() => {
    const storageKey = `onboarding_license_key_${branchId || "default"}`;
    const savedKey = localStorage.getItem(storageKey);

    if (savedKey) {
      return savedKey;
    }

    const newKey = generateLicenseKey();
    localStorage.setItem(storageKey, newKey);
    return newKey;
  }, [branchId]);
  const defaultOid = "678e4686ba011b117aaea9a9";
  const clientOid = branchId || "69c39f863ebbada516f2923b";

  const initialCardObjectText = `"createdAt": {
    "$date": "${today}"
  },
  "updatedAt": {
    "$date": "${today}"
  },
  "name": "${branchName || "N/A"}",
  "email": "${adminEmail || "N/A"}",
  "phone": "${adminPhone || "N/A"}"`;

  const secondObjectText = `"clearACT": false,
  "status": "Active",
  "createdAt": {
    "$date": "${today}"
  },
  "updatedAt": {
    "$date": "${today}"
  },
  "applicationId": {
    "$oid": "${defaultOid}"
  },
  "clientId": {
    "$oid": "${clientOid}"
  },
  "licenseTypeId": {
    "$oid": "${defaultOid}"
  },
  "licenseKey": "${licenseKey}",
  "startDate": {
    "$date": "${startDate}"
  },
  "endDate": {
    "$date": "${endDate}"
  }`;

  const thirdObjectText = `"license": {
  "token1": "${defaultOid}",
  "token2": "${clientOid}",
  "licenseKey": "${licenseKey}"
}`;

  async function handleCopyObject() {
    const result = await copyToClipboard(initialCardObjectText, { label: "Initial onboarding object" });
    if (result.success) {
      setCopiedInitial(true);
      setTimeout(() => {
        setCopiedInitial(false);
      }, 1800);
    } else {
      showToast(result.message || "Failed to copy object", "error");
    }
  }

  async function handleCopySecondObject() {
    const result = await copyToClipboard(secondObjectText, { label: "License date object" });
    if (result.success) {
      setCopiedSecond(true);
      setTimeout(() => {
        setCopiedSecond(false);
      }, 1800);
    } else {
      showToast(result.message || "Failed to copy object", "error");
    }
  }

  async function handleCopyThirdObject() {
    const result = await copyToClipboard(thirdObjectText, { label: "License token object" });
    if (result.success) {
      setCopiedThird(true);
      setTimeout(() => {
        setCopiedThird(false);
      }, 1800);
    } else {
      showToast(result.message || "Failed to copy object", "error");
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
        columnGap: "clamp(16px, 4vw, 64px)",
        rowGap: "40px",
        alignItems: "start",
        marginTop: "24px"
      }}
    >
      <div
        style={{
          backgroundColor: "#20232b",
          border: "1px solid #3a3f4b",
          borderRadius: "10px",
          padding: "20px",
          color: "#fff",
          maxWidth: "100%",
          width: "100%",
          position: "relative"
        }}
      >
        <div style={{ marginBottom: "16px" }}>
          <h2
            onClick={handleCopyObject}
            style={{ margin: 0, cursor: "pointer", userSelect: "none" }}
            title="Click to copy object"
          >
            Initial Object
          </h2>
        </div>

        {copiedInitial && (
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
              color: "#fff",
              padding: "6px 10px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
              border: "1px solid rgba(255,255,255,0.2)",
              boxShadow: "0 4px 12px rgba(24, 119, 24, 0.35)"
            }}
          >
            Object copied
          </div>
        )}

        <pre
          style={{
            margin: 0,
            backgroundColor: "#12151d",
            border: "1px solid #2d3341",
            borderRadius: "8px",
            padding: "14px",
            color: "#e5e7eb",
            fontSize: "14px",
            lineHeight: 1.6,
            overflowX: "auto"
          }}
        >
          {initialCardObjectText}
        </pre>
      </div>

      <div
        style={{
          backgroundColor: "#20232b",
          border: "1px solid #3a3f4b",
          borderRadius: "10px",
          padding: "20px",
          color: "#fff",
          maxWidth: "100%",
          width: "100%",
          position: "relative"
        }}
      >
        <div style={{ marginBottom: "16px" }}>
          <h2
            onClick={handleCopySecondObject}
            style={{ margin: 0, cursor: "pointer", userSelect: "none" }}
            title="Click to copy object"
          >
            Second Object
          </h2>
        </div>

        {copiedSecond && (
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
              color: "#fff",
              padding: "6px 10px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
              border: "1px solid rgba(255,255,255,0.2)",
              boxShadow: "0 4px 12px rgba(24, 119, 24, 0.35)"
            }}
          >
            Object copied
          </div>
        )}

        <pre
          style={{
            margin: 0,
            backgroundColor: "#12151d",
            border: "1px solid #2d3341",
            borderRadius: "8px",
            padding: "14px",
            color: "#e5e7eb",
            fontSize: "14px",
            lineHeight: 1.6,
            overflowX: "auto"
          }}
        >
          {secondObjectText}
        </pre>
      </div>

      <div
        style={{
          backgroundColor: "#20232b",
          border: "1px solid #3a3f4b",
          borderRadius: "10px",
          padding: "20px",
          color: "#fff",
          maxWidth: "100%",
          width: "100%",
          position: "relative"
        }}
      >
        <div style={{ marginBottom: "16px" }}>
          <h2
            onClick={handleCopyThirdObject}
            style={{ margin: 0, cursor: "pointer", userSelect: "none" }}
            title="Click to copy object"
          >
            Third Object
          </h2>
        </div>

        {copiedThird && (
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "linear-gradient(135deg, #187718 0%, #20a020 100%)",
              color: "#fff",
              padding: "6px 10px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
              border: "1px solid rgba(255,255,255,0.2)",
              boxShadow: "0 4px 12px rgba(24, 119, 24, 0.35)"
            }}
          >
            Object copied
          </div>
        )}

        <pre
          style={{
            margin: 0,
            backgroundColor: "#12151d",
            border: "1px solid #2d3341",
            borderRadius: "8px",
            padding: "14px",
            color: "#e5e7eb",
            fontSize: "14px",
            lineHeight: 1.6,
            overflowX: "auto"
          }}
        >
          {thirdObjectText}
        </pre>
      </div>
    </div>
  );
}

export default OnboardingObject;
